import { useEffect, useState } from 'react'
import {
    Box, Button, Typography, Modal, TextField, useMediaQuery,
    FormControl, InputLabel, Select, MenuItem, FormHelperText,
} from '@mui/material'
import Swal from 'sweetalert2'

import { isNsfwUrl, NSFW_WARNING } from '../../../../../../utils/nsfwGuard'
import { isValidLinkUrl, INVALID_URL_WARNING } from '../../../../../../utils/urlValidation'
import { startCheckout } from '../../../../../../services/api/StripeDataService'


const MONEY_TYPES = ['SPONSOR', 'SHOP', 'AFFILIATE', 'OTHER']

export function UpdateLinkButtonModal({ open, onClose, onUpdateLink, title, url, moneyType = '', isPro, onMoneyTypeChange }) {
    const [newTitle, setNewTitle] = useState(title)
    const [newUrl, setNewUrl] = useState(url)
    const [newMoneyType, setNewMoneyType] = useState(moneyType)
    const [urlError, setUrlError] = useState('')
    const isMobile = useMediaQuery('(max-width:1050px)')

    // Re-sync from props each time the modal opens (handles tags changed elsewhere).
    useEffect(() => {
        if (open) {
            setNewTitle(title)
            setNewUrl(url)
            setNewMoneyType(moneyType)
            setUrlError('')
        }
    }, [open, title, url, moneyType])

    const handleUpdateLink = async () => {
        if (newTitle && newUrl) {
            // Only allow real links — reject code snippets / non-URL input.
            if (!isValidLinkUrl(newUrl)) {
                setUrlError(INVALID_URL_WARNING)
                return
            }
            // Block links to NSFW / 18+ sites before anything is saved.
            if (isNsfwUrl(newUrl)) {
                setUrlError(NSFW_WARNING)
                return
            }
            onUpdateLink({ newTitle, newUrl })
            if (isPro && newMoneyType !== (moneyType || '')) {
                await onMoneyTypeChange?.(newMoneyType)
            }
            onClose()
        }
    }

    const handleUpgrade = async () => {
        try {
            await startCheckout('pro', true)
        } catch (error) {
            Swal.fire({
                title: "Can't Proceed to Checkout",
                text: 'Something went wrong. Please try again later.',
                icon: 'error',
            })
        }
    }

    return (
        <Modal
            open={open}
            onClose={onClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <Box sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: isMobile ? 300 : 380,
                bgcolor: 'background.paper',
                borderRadius: '30px',
                boxShadow: 24,
                p: 4,
            }}>
                <Typography id="modal-modal-title" variant="h6" component="h2">
                    Update link
                </Typography>
                <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="title"
                    label="Title"
                    name="title"
                    autoComplete="title"
                    autoFocus
                    value={newTitle}
                    placeholder={title}
                    onChange={(e) => setNewTitle(e.target.value)}
                />
                <TextField
                    margin="normal"
                    required
                    fullWidth
                    id="url"
                    label="URL"
                    name="url"
                    autoComplete="url"
                    value={newUrl}
                    placeholder={url}
                    error={!!urlError}
                    helperText={urlError}
                    onChange={(e) => { setNewUrl(e.target.value); setUrlError('') }}
                />

                <FormControl fullWidth margin="normal" disabled={!isPro}>
                    <InputLabel id="money-type-label">Revenue Links</InputLabel>
                    <Select
                        labelId="money-type-label"
                        label="Revenue link"
                        value={newMoneyType}
                        onChange={(e) => setNewMoneyType(e.target.value)}
                        displayEmpty
                    >
                        {MONEY_TYPES.map((t) => (
                            <MenuItem key={t} value={t}>{t.charAt(0) + t.slice(1).toLowerCase()}</MenuItem>
                        ))}
                    </Select>
                    <FormHelperText>
                        {isPro
                            ? "Mark links that earn you money."
                            :
                            <>
                                Tagging revenue links is a Pro feature.{' '}
                                <Box
                                    component="span"
                                    onClick={handleUpgrade}
                                    sx={{
                                        color: 'primary.main',
                                        fontWeight: 500,
                                        cursor: 'pointer',
                                        textDecoration: 'underline',
                                        pointerEvents: 'auto',
                                        '&:hover': { color: 'primary.dark' },
                                    }}
                                >
                                    Upgrade to Pro
                                </Box>
                            </>
                        }
                    </FormHelperText>
                </FormControl>

                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleUpdateLink}
                    fullWidth
                    sx={{
                        display: 'block',
                        mx: 'auto',
                        mt: 2,
                        padding: '.6rem 1rem',
                        borderRadius: '40px',
                        boxShadow: "2px 3px 0px #000",
                        textTransform: 'none'
                    }}
                >
                    Update
                </Button>
            </Box>
        </Modal>
    )
}
